import {
  acceptCompanyInvitation,
  acceptInvitationWithAccountCreation,
  createCompany,
  createCompanyUser,
  createCompanyWithOwnerInvite,
  deleteCompany,
  removeCompanyUser,
  resendCompanyInvitation,
  updateCompany,
  updateCompanyStatus,
  validateEmailForOwnership,
} from '@/services/companies'
import type { Company, CompanyInput } from '@/types'
import type { CompanyUser, CompanyUserInput } from '@/types/companyUser'
import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { companyKeys } from './query-keys'

// Create company mutation
export const useCreateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ companyInput, ownerID }: { companyInput: CompanyInput; ownerID: string }) =>
      createCompany(companyInput, ownerID),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(companyKeys.detail(data.id!), data)
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: companyKeys.userCompanies(variables.ownerID) })
      queryClient.invalidateQueries({ queryKey: companyKeys.userJoinedCompanies(variables.ownerID) })
      toast.success('Company created successfully')
    },
    onError: (error) => {
      toast.error('Failed to create company. Please try again.')
    },
  })
}

// Update company mutation with optimistic updates
export const useUpdateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ companyId, companyInput }: { companyId: string; companyInput: Partial<CompanyInput> }) =>
      updateCompany(companyId, companyInput),
    onMutate: async ({ companyId, companyInput }) => {
      await queryClient.cancelQueries({ queryKey: companyKeys.detail(companyId) })
      const previousData = queryClient.getQueryData(companyKeys.detail(companyId))
      queryClient.setQueryData(companyKeys.detail(companyId), (old: Company | undefined) =>
        old ? { ...old, ...companyInput, updatedAt: new Date() as any } : undefined,
      )
      return { previousData, companyId }
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(companyKeys.detail(variables.companyId), data)
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: companyKeys.all })
      toast.success('Company updated successfully')
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(companyKeys.detail(context.companyId), context.previousData)
      }
      toast.error('Failed to update company. Please try again.')
    },
  })
}

// Update company status with optimistic updates
export const useUpdateCompanyStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ companyId, status }: { companyId: string; status: boolean }) =>
      updateCompanyStatus(companyId, status),
    onMutate: async ({ companyId, status }) => {
      await queryClient.cancelQueries({ queryKey: companyKeys.detail(companyId) })
      const previousData = queryClient.getQueryData(companyKeys.detail(companyId))
      queryClient.setQueryData(companyKeys.detail(companyId), (old: Company | undefined) =>
        old
          ? {
              ...old,
              status,
              updatedAt: new Date() as any,
              ...(status === false ? { canCreatePosts: false } : {}),
            }
          : undefined,
      )
      return { previousData, companyId }
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(companyKeys.detail(variables.companyId), data)
      queryClient.invalidateQueries({ queryKey: companyKeys.all })
      toast.success('Company status updated successfully')
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(companyKeys.detail(context.companyId), context.previousData)
      }
      toast.error('Failed to update company status. Please try again.')
    },
  })
}

// Delete company mutation
export const useDeleteCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (companyId: string) => deleteCompany(companyId),
    onMutate: async (companyId) => {
      await queryClient.cancelQueries({ queryKey: companyKeys.detail(companyId) })
      const previousCompany = queryClient.getQueryData<Company>(companyKeys.detail(companyId))
      queryClient.removeQueries({ queryKey: companyKeys.detail(companyId) })
      return { previousCompany, companyId }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: companyKeys.all })
      toast.success('Company deleted successfully')
    },
    onError: (error, companyId, context) => {
      if (context?.previousCompany) {
        queryClient.setQueryData(companyKeys.detail(companyId), context.previousCompany)
      }
      toast.error('Failed to delete company. Please try again.')
    },
  })
}

// Create company user mutation
export const useCreateCompanyUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CompanyUserInput) => createCompanyUser(input),
    onMutate: async (variables) => {
      if (!variables.companyID) return {}

      await queryClient.cancelQueries({ queryKey: companyKeys.companyUsers(variables.companyID) })
      const previousUsers = queryClient.getQueryData<CompanyUser[]>(companyKeys.companyUsers(variables.companyID))

      if (previousUsers) {
        const optimisticUser: CompanyUser = {
          ...variables,
          id: `temp-${Date.now()}`,
          createdAt: new Date() as any,
        }
        queryClient.setQueryData(companyKeys.companyUsers(variables.companyID), [...previousUsers, optimisticUser])
      }

      return { previousUsers, companyID: variables.companyID }
    },
    onSuccess: (data, variables) => {
      if (variables.companyID) {
        queryClient.invalidateQueries({ queryKey: companyKeys.companyUsers(variables.companyID) })
      }
      if (variables.userID) {
        queryClient.invalidateQueries({ queryKey: companyKeys.userCompanies(variables.userID) })
        queryClient.invalidateQueries({ queryKey: companyKeys.userJoinedCompanies(variables.userID) })
      }
      toast.success('User added to company successfully')
    },
    onError: (error, variables, context) => {
      if (context?.companyID && context?.previousUsers) {
        queryClient.setQueryData(companyKeys.companyUsers(context.companyID), context.previousUsers)
      }
      toast.error('Failed to add user to company. Please try again.')
    },
  })
}

// Remove company user mutation
export const useRemoveCompanyUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ companyId, userId }: { companyId: string; userId: string }) => {
      await removeCompanyUser(companyId, userId)
    },
    onMutate: async ({ companyId, userId }) => {
      await queryClient.cancelQueries({ queryKey: companyKeys.companyUsers(companyId) })
      const previousUsers = queryClient.getQueryData<CompanyUser[]>(companyKeys.companyUsers(companyId))

      if (previousUsers) {
        const updatedUsers = previousUsers.filter((user) => user.userID !== userId)
        queryClient.setQueryData(companyKeys.companyUsers(companyId), updatedUsers)
      }

      return { previousUsers, companyId, userId }
    },
    onSuccess: (_, { companyId, userId }) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.companyUsers(companyId) })
      queryClient.invalidateQueries({ queryKey: companyKeys.userCompanies(userId) })
      queryClient.invalidateQueries({ queryKey: companyKeys.userJoinedCompanies(userId) })
      toast.success('User removed from company successfully')
    },
    onError: (error, { companyId }, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(companyKeys.companyUsers(companyId), context.previousUsers)
      }
      toast.error('Failed to remove user from company. Please try again.')
    },
  })
}

export interface CreateCompanyWithInviteInput {
  companyInput: CompanyInput
  ownerEmail: string
  superAdminId: string
}

export const useCreateCompanyWithInvite = (
  options?: UseMutationOptions<Company, Error, CreateCompanyWithInviteInput>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ companyInput, ownerEmail, superAdminId }: CreateCompanyWithInviteInput) =>
      createCompanyWithOwnerInvite(companyInput, ownerEmail, superAdminId),
    onSuccess: (data) => {
      queryClient.setQueryData(companyKeys.detail(data.id!), data)
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() })
      toast.success('Company created and invitation sent successfully')
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create company. Please try again.')
    },
    ...options,
  })
}

export const useValidateEmailForOwnership = (
  options?: UseMutationOptions<{ canBeOwner: boolean; reason?: string }, Error, string>,
) => {
  return useMutation({
    mutationFn: validateEmailForOwnership,
    onError: (error) => {
      toast.error('Failed to validate email. Please try again.')
    },
    ...options,
  })
}

export const useResendCompanyInvitation = (
  options?: UseMutationOptions<Company, Error, { companyId: string; superAdminId: string }>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ companyId, superAdminId }: { companyId: string; superAdminId: string }) =>
      resendCompanyInvitation(companyId, superAdminId),
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.all })
      queryClient.invalidateQueries({ queryKey: companyKeys.detail(company.id || '') })

      toast.success('Invitation resent successfully!')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to resend invitation')
    },
    ...options,
  })
}

export const useAcceptCompanyInvitation = (
  options?: UseMutationOptions<Company, Error, { token: string; userId: string }>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ token, userId }: { token: string; userId: string }) => acceptCompanyInvitation(token, userId),
    onSuccess: (company, { userId }) => {
      queryClient.setQueryData(companyKeys.detail(company.id!), company)
      queryClient.invalidateQueries({ queryKey: companyKeys.all })
      queryClient.invalidateQueries({ queryKey: companyKeys.userCompanies(userId) })
      queryClient.invalidateQueries({ queryKey: companyKeys.userJoinedCompanies(userId) })

      toast.success('Company ownership accepted successfully!')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to accept invitation')
    },
    ...options,
  })
}

export const useAcceptInvitationWithAccountCreation = (
  options?: UseMutationOptions<
    { company: Company; user: any },
    Error,
    { token: string; fullName: string; password: string }
  >,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ token, fullName, password }: { token: string; fullName: string; password: string }) =>
      acceptInvitationWithAccountCreation(token, fullName, password),
    onSuccess: ({ company, user }) => {
      queryClient.setQueryData(companyKeys.detail(company.id!), company)
      queryClient.invalidateQueries({ queryKey: companyKeys.all })
      queryClient.invalidateQueries({ queryKey: companyKeys.userCompanies(user.id) })
      queryClient.invalidateQueries({ queryKey: companyKeys.userJoinedCompanies(user.id) })

      toast.success('Account created and company ownership accepted successfully!')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create account and accept invitation')
    },
    ...options,
  })
}
